// Super Admin: Create or edit a survey — name, description, ordered questions from pool.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import Layout from '../Layout';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { SURVEY_QUESTIONS_SEED } from '../../data/surveyQuestionsSeed';

const SurveyEdit = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const isNew = !surveyId;
  const { isSuperAdmin } = useAuth();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedQuestions, setSelectedQuestions] = useState([]); // { questionId, questionText, order }
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addQuestionSource, setAddQuestionSource] = useState('pool'); // 'pool' | 'curated'

  useEffect(() => {
    if (!isSuperAdmin()) return;
    if (isNew) {
      loadPool().then(() => setLoading(false));
    } else {
      loadPool();
      loadSurvey();
    }
  }, [surveyId, isNew]);

  const loadSurvey = async () => {
    if (!surveyId) return;
    try {
      setLoading(true);
      const surveyRef = doc(db, 'surveys', surveyId);
      const surveySnap = await getDoc(surveyRef);
      if (!surveySnap.exists()) {
        toast.error('Survey not found');
        navigate('/admin/surveys');
        return;
      }
      const data = surveySnap.data();
      setName(data.name || '');
      setDescription(data.description || '');

      const questionsRef = collection(db, 'surveys', surveyId, 'questions');
      let questionsSnap;
      try {
        const q = query(questionsRef, orderBy('order', 'asc'));
        questionsSnap = await getDocs(q);
      } catch {
        questionsSnap = await getDocs(questionsRef);
      }
      const questions = questionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      questions.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSelectedQuestions(
        questions.map((q, i) => ({
          questionId: q.questionId ?? null,
          questionText: q.questionText || '',
          order: i,
        }))
      );
    } catch (err) {
      console.error('Error loading survey:', err);
      toast.error('Failed to load survey');
      navigate('/admin/surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadPool = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'surveyQuestions'));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setPool(list);
    } catch (err) {
      console.error('Error loading question pool:', err);
      toast.error('Failed to load question pool');
    }
  };


  const addQuestionFromPool = (q) => {
    const order = selectedQuestions.length;
    setSelectedQuestions((prev) => [
      ...prev,
      { questionId: q.id, questionText: q.text || '', order },
    ]);
    setShowAddModal(false);
  };

  const addQuestionFromCurated = (text) => {
    const order = selectedQuestions.length;
    setSelectedQuestions((prev) => [
      ...prev,
      { questionId: null, questionText: text.trim(), order },
    ]);
    setShowAddModal(false);
  };

  const removeQuestion = (index) => {
    setSelectedQuestions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((item, i) => ({ ...item, order: i }));
    });
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setSelectedQuestions((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map((item, i) => ({ ...item, order: i }));
    });
  };

  const moveDown = (index) => {
    if (index >= selectedQuestions.length - 1) return;
    setSelectedQuestions((prev) => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map((item, i) => ({ ...item, order: i }));
    });
  };

  const poolAvailable = pool.filter(
    (p) => !selectedQuestions.some((s) => s.questionId === p.id)
  );

  const selectedTexts = new Set(
    selectedQuestions.map((sq) => (sq.questionText || '').trim().toLowerCase())
  );
  const curatedAvailable = SURVEY_QUESTIONS_SEED.filter(
    (text) => text && !selectedTexts.has(text.trim().toLowerCase())
  );

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Survey name is required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const surveyRef = await addDoc(collection(db, 'surveys'), {
          name: trimmedName,
          description: description.trim() || null,
          isBoilerplate: true,
          createdAt: serverTimestamp(),
          createdBy: 'superAdmin',
        });
        const id = surveyRef.id;
        const questionsRef = collection(db, 'surveys', id, 'questions');
        for (let i = 0; i < selectedQuestions.length; i++) {
          const sq = selectedQuestions[i];
          await addDoc(questionsRef, {
            ...(sq.questionId != null && { questionId: sq.questionId }),
            questionText: sq.questionText,
            order: i,
          });
        }
        toast.success('Survey created');
        navigate('/admin/surveys');
      } else {
        await updateDoc(doc(db, 'surveys', surveyId), {
          name: trimmedName,
          description: description.trim() || null,
          updatedAt: serverTimestamp(),
        });
        const questionsRef = collection(db, 'surveys', surveyId, 'questions');
        const existing = await getDocs(questionsRef);
        for (const d of existing.docs) {
          await deleteDoc(doc(db, 'surveys', surveyId, 'questions', d.id));
        }
        for (let i = 0; i < selectedQuestions.length; i++) {
          const sq = selectedQuestions[i];
          await addDoc(questionsRef, {
            ...(sq.questionId != null && { questionId: sq.questionId }),
            questionText: sq.questionText,
            order: i,
          });
        }
        toast.success('Survey updated');
        navigate('/admin/surveys');
      }
    } catch (err) {
      console.error('Error saving survey:', err);
      toast.error('Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You don&apos;t have permission to access this area.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isNew && loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="mt-4 text-gray-600">Loading survey...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/surveys')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Surveys
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isNew ? 'Create Survey' : 'Edit Survey'}
          </h1>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Survey name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="e.g. Exploring Faith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Short description for disciplemakers"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Questions</label>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add question
              </button>
            </div>
            {selectedQuestions.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 border border-dashed border-gray-200 rounded-md text-center">
                No questions yet. Click &quot;Add question&quot; to choose from the pool.
              </p>
            ) : (
              <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
                {selectedQuestions.map((sq, index) => (
                  <li key={`${sq.questionId ?? 'c'}-${index}`} className="px-4 py-3 flex items-center justify-between gap-2 bg-white hover:bg-gray-50">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-500 w-8">{index + 1}.</span>
                      <span className="text-gray-900 truncate">{sq.questionText}</span>
                    </div>
                    <div className="flex items-center shrink-0">
                      <button
                        type="button"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                        aria-label="Move up"
                      >
                        <ChevronUpIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveDown(index)}
                        disabled={index === selectedQuestions.length - 1}
                        className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                        aria-label="Move down"
                      >
                        <ChevronDownIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeQuestion(index)}
                        className="p-1 rounded text-red-600 hover:bg-red-50"
                        aria-label="Remove"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/surveys')}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isNew ? 'Create Survey' : 'Save Changes'}
            </button>
          </div>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[80vh] flex flex-col">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add a question</h3>
              <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
                <button
                  type="button"
                  onClick={() => setAddQuestionSource('pool')}
                  className={`px-3 py-1.5 rounded text-sm font-medium ${
                    addQuestionSource === 'pool'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  From pool
                </button>
                <button
                  type="button"
                  onClick={() => setAddQuestionSource('curated')}
                  className={`px-3 py-1.5 rounded text-sm font-medium ${
                    addQuestionSource === 'curated'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  From curated list ({SURVEY_QUESTIONS_SEED.length} questions)
                </button>
              </div>
              {addQuestionSource === 'pool' ? (
                poolAvailable.length === 0 ? (
                  <p className="text-gray-600">No more questions in the pool, or all have been added.</p>
                ) : (
                  <ul className="overflow-y-auto flex-1 border border-gray-200 rounded-md divide-y divide-gray-200">
                    {poolAvailable.map((q) => (
                      <li key={q.id}>
                        <button
                          type="button"
                          onClick={() => addQuestionFromPool(q)}
                          className="w-full px-4 py-3 text-left text-sm text-gray-900 hover:bg-blue-50"
                        >
                          {q.text}
                          {q.category && (
                            <span className="ml-2 text-xs text-gray-500">({q.category})</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              ) : curatedAvailable.length === 0 ? (
                <p className="text-gray-600">All curated questions are already in this survey.</p>
              ) : (
                <ul className="overflow-y-auto flex-1 border border-gray-200 rounded-md divide-y divide-gray-200">
                  {curatedAvailable.map((text, idx) => (
                    <li key={idx}>
                      <button
                        type="button"
                        onClick={() => addQuestionFromCurated(text)}
                        className="w-full px-4 py-3 text-left text-sm text-gray-900 hover:bg-blue-50"
                      >
                        {text}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SurveyEdit;
